export {};

const INIT_FLAG = 'stgbRevealSliderInit';
const DRAGGING_CLASS = 'is-dragging';

const clamp = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max);

const initBlock = (block: HTMLElement) => {
    if (block.dataset[INIT_FLAG] === 'true') return;

    const bar = block.querySelector<HTMLElement>('.stgb-reveal-slider-bar');
    const track = block.querySelector<HTMLElement>('.stgb-reveal-slider-bar-track');
    const handle = block.querySelector<HTMLButtonElement>('.stgb-reveal-slider-handle');
    const panels = Array.from(block.querySelectorAll<HTMLElement>('.stgb-reveal-slider-panel'));
    const count = panels.length;

    if (!bar || !track || !handle || count === 0) return;

    let progress = 0;

    const setProgress = (value: number) => {
        progress = clamp(value, 0, 1);
        block.style.setProperty('--stgb-rs-progress', String(progress));
    };

    const isHorizontal = () => {
        const rect = bar.getBoundingClientRect();
        return rect.width >= rect.height;
    };

    // Map a pointer position to 0..1 along the track — the inner element
    // inset from the bar's ends, which defines the handle's travel range.
    const pointerProgress = (event: { clientX: number; clientY: number }) => {
        const rect = track.getBoundingClientRect();
        const horizontal = rect.width >= rect.height;
        const position = horizontal ? event.clientX - rect.left : event.clientY - rect.top;
        const span = horizontal ? rect.width : rect.height;
        if (span <= 0) return 0;
        return clamp(position / span, 0, 1);
    };

    const activeIndex = () => Math.round(progress * (count - 1));

    const applyActive = (index: number) => {
        handle.setAttribute('aria-valuenow', String(index));

        const title = panels[index]
            ?.querySelector('.stgb-reveal-slider-title')
            ?.textContent?.trim();
        if (title) {
            handle.setAttribute('aria-valuetext', title);
        } else {
            handle.removeAttribute('aria-valuetext');
        }

        panels.forEach((panel, i) => {
            const isActive = i === index;
            panel.classList.toggle('is-active', isActive);

            if (isActive) {
                panel.removeAttribute('aria-hidden');
            } else {
                panel.setAttribute('aria-hidden', 'true');
            }

            panel.querySelectorAll<HTMLElement>('a, button').forEach((el) => {
                if (isActive) {
                    el.removeAttribute('tabindex');
                } else {
                    el.setAttribute('tabindex', '-1');
                }
            });
        });
    };

    const snapTo = (index: number) => {
        const next = clamp(index, 0, count - 1);
        setProgress(count > 1 ? next / (count - 1) : 0);
        applyActive(next);
    };

    const syncOrientation = () => {
        handle.setAttribute('aria-orientation', isHorizontal() ? 'horizontal' : 'vertical');
    };

    handle.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        // preventDefault suppresses native focus; restore it so arrow keys
        // work right after a drag.
        handle.focus({ preventScroll: true });
        handle.setPointerCapture(event.pointerId);
        block.classList.add(DRAGGING_CLASS);
    });

    handle.addEventListener('pointermove', (event) => {
        if (!handle.hasPointerCapture(event.pointerId)) return;
        setProgress(pointerProgress(event));
    });

    const endDrag = (event: PointerEvent) => {
        if (!handle.hasPointerCapture(event.pointerId)) return;
        handle.releasePointerCapture(event.pointerId);
        block.classList.remove(DRAGGING_CLASS);
        snapTo(activeIndex());
    };

    handle.addEventListener('pointerup', endDrag);
    handle.addEventListener('pointercancel', endDrag);

    handle.addEventListener('keydown', (event) => {
        let next: number;
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                next = activeIndex() + 1;
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                next = activeIndex() - 1;
                break;
            case 'Home':
                next = 0;
                break;
            case 'End':
                next = count - 1;
                break;
            default:
                return;
        }
        event.preventDefault();
        snapTo(next);
    });

    // Clicking anywhere on the bar jumps to the nearest snap point. Clicks
    // re-targeted to the handle by pointer capture are ignored.
    bar.addEventListener('click', (event) => {
        const target = event.target as HTMLElement | null;
        if (target && target.closest('.stgb-reveal-slider-handle')) return;
        snapTo(Math.round(pointerProgress(event) * (count - 1)));
    });

    window.addEventListener('resize', syncOrientation);

    syncOrientation();
    snapTo(0);
    block.dataset[INIT_FLAG] = 'true';
};

const initAll = () => {
    document
        .querySelectorAll<HTMLElement>('.stgb-reveal-slider')
        .forEach(initBlock);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
} else {
    initAll();
}
